/**
 * The app's one keyboard focus indicator.
 *
 * A class-name constant, not a type or a component — it sits beside `badgeTones`,
 * `listPageTheme` and `fieldTheme` for the same reason: several components need the identical
 * string and a second copy is a second answer.
 *
 * ### Why it exists (5H.1)
 *
 * The shell primitives — every sidebar row, the group toggles, the navbar's icon buttons, the
 * user chip and the account-menu rows — carried no focus styling at all and fell back to
 * whatever ring the browser draws. That ring differs per engine, and on the indigo
 * `ds-action-primary` fill of the *active* sidebar row it is close to invisible, which is the
 * one row a keyboard user is most likely to be on.
 *
 * ### Why `ds-border-focus`
 *
 * It is the token the design system already reserves for this, and it is the only one that
 * clears WCAG 1.4.11's 3:1 non-text threshold against **every** surface the chrome puts
 * behind it, in both themes:
 *
 * | ring                | against                    | ratio |
 * | ------------------- | -------------------------- | ----- |
 * | `#438CF3` (light)   | white sidebar              | 3.34  |
 * | `#438CF3` (light)   | active row `#151AAE`       | 3.51  |
 * | `#5F9EF5` (dark)    | dark sidebar `#1A1D3A`     | 6.01  |
 * | `#5F9EF5` (dark)    | dark active row `#1D23BE`  | 3.78  |
 *
 * ### `focus-visible`, not `focus`
 *
 * A pointer click on a nav row should not leave a ring behind it; `focus-visible` is what
 * distinguishes keyboard traversal from a mouse press. The ring is drawn outside the row via
 * `ring-offset`, so it never sits on top of the label.
 *
 * Pass the surface the control sits on as `offset`, so the offset gap matches its background
 * instead of defaulting to white and cutting a white notch out of a dark theme.
 */
export function focusRingClass(offset: 'surface' | 'sidebar' | 'page' = 'surface') {
  const offsetClass = {
    surface: 'focus-visible:ring-offset-ds-bg-surface',
    sidebar: 'focus-visible:ring-offset-ds-bg-sidebar',
    page: 'focus-visible:ring-offset-ds-bg-page',
  }[offset];

  return `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border-focus focus-visible:ring-offset-2 ${offsetClass}`;
}

/** The common case: a control on a card or the navbar. */
export const FOCUS_RING = focusRingClass('surface');
